import { useGraphStore } from "@modules/data/stores/graph.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"
import { Source, WorkflowEnedis } from "@types"
import { Services } from "@services"
import { computed, inject, ref } from "vue"
import * as Sentry from "@sentry/vue"
import { useI18n } from "vue-i18n"
import { formatIdSource } from "@core/helpers"
import { SOURCES_TYPES } from "@core/constants"
import { useFetchWrapper } from "@core/composables"

/** Workflows dont le libellé est défini explicitement dans les messages i18n ci-dessous. */
const WORKFLOW_ENEDIS_NAMES_WITH_DEDICATED_COPY = new Set([
    "historique_onboarding",
    "update_once",
])

export const useWorkflowEnedisMessage = (
    type: "building" | "source" = "building"
) => {
    const graphStore = useGraphStore()
    const buildingStore = useBuildingStore()
    const sourceStore = useSourceStore()

    // useScope: 'global' : un composant ne peut avoir qu'un seul composer i18n « local ».
    // PageBuildingData (entre autres) appelle déjà useI18n local avant ce composable ;
    // sans global, le 2e appel est ignoré et les clés workflow restent introuvables ([clé]).
    const { t } = useI18n({
        useScope: "global",
        messages: {
            fr: {
                update_once:
                    "Une mise à jour des données a été demandée pour le compteur {idsource}. Dès qu'Enedis nous enverra les nouvelles données, l'application sera mise à jour automatiquement. Cela prend de quelques minutes à quelques heures. | Une mise à jour des données a été demandée pour les compteurs {idsource}. Dès qu'Enedis nous les enverra, l'application sera mise à jour automatiquement. Cela prend de quelques minutes à quelques heures.",
                historique_onboarding:
                    "La récupération des données de votre compteur {idsource} a été demandée à Enedis. Dès qu'Enedis nous enverra les données, l'application sera mise à jour automatiquement. Cela prend de quelques minutes à quelques heures. | Les données des compteurs {idsource} ont été demandées à Enedis. Dès qu'Enedis nous les enverra, l'application sera mise à jour automatiquement. Cela prend de quelques minutes à quelques heures.",
                data_retrieval_in_progress:
                    "Les données de votre compteur {idsource} sont en cours de récupération auprès d'Enedis. Le chargement sera terminé lorsque ce message ne sera plus affiché. | Les données de vos compteurs {idsource} sont en cours de récupération auprès d'Enedis. Le chargement sera terminé lorsque ce message ne sera plus affiché.",
                cdc_activating_delayed:
                    "La courbe de charge du compteur {idsource} n'était pas activée chez Enedis. L'activation a été demandée — comptez jusqu'à 3 jours avant la première remontée des mesures. | La courbe de charge des compteurs {idsource} n'était pas activée chez Enedis. L'activation a été demandée — comptez jusqu'à 3 jours avant les premières remontées des mesures.",
            },
        },
    })

    const {
        $getAllEnedisWorkflowByBuildingId,
        $getAllEnedisWorkflowBySourceId,
    } = inject<Services>("services")!

    const workflowEnedisList = ref<WorkflowEnedis[]>([])

    /**
     * Récupère les workflows Enedis pour un bâtiment ou une source
     * en fonction du type. Récupère l'identifiant dans le store graph
     */
    const { fetch, isFetching, hasError } = useFetchWrapper(async () => {
        const service =
            type === "building"
                ? $getAllEnedisWorkflowByBuildingId
                : $getAllEnedisWorkflowBySourceId

        const id =
            type === "building"
                ? buildingStore.building?._id
                : graphStore.currentSourceId

        try {
            workflowEnedisList.value = await service(id!)
        } catch (err) {
            Sentry.captureException(err, {
                extra: {
                    type,
                    id,
                },
            })
        }
    })

    /**
     * PDL (dans le périmètre du bâtiment ou de la source courante) dont la courbe
     * de charge est en cours d'activation côté Enedis (`historical.state === "delayed"`).
     */
    const delayedPdls = computed<Source[]>(() => {
        const candidates: (Source | undefined)[] =
            type === "building"
                ? buildingStore.getSources
                : [
                      graphStore.currentSourceId
                          ? sourceStore.getSourceById(
                                graphStore.currentSourceId
                            )
                          : undefined,
                  ]
        return candidates.filter(
            (source): source is Source =>
                source?.type === SOURCES_TYPES.PDL &&
                source?.informations?.pdl?.data_retrieval?.historical?.state ===
                    "delayed"
        )
    })

    /**
     * Récupère les messages des workflows Enedis
     */
    const getWorkflowEnedisMessageList = computed(() => {
        const messages: string[] = []

        // regroupe les workflow par "name"
        const workflowGroupByName = workflowEnedisList.value.reduce(
            (acc, workflow) => {
                if (!acc[workflow.name]) {
                    acc[workflow.name] = []
                }
                acc[workflow.name].push(workflow)
                return acc
            },
            {} as Record<string, WorkflowEnedis[]>
        )
        const formatIdsources = (workflows: WorkflowEnedis[]) =>
            workflows
                .map((w) => w.idsource)
                .filter((v, i, a) => a.indexOf(v) === i)
                .map((idsource) => formatIdSource(idsource, SOURCES_TYPES.PDL))
                .join(", ")

        const unknownWorkflows: WorkflowEnedis[] = []

        for (const [name, workflows] of Object.entries(workflowGroupByName)) {
            if (WORKFLOW_ENEDIS_NAMES_WITH_DEDICATED_COPY.has(name)) {
                messages.push(
                    t(name, {
                        idsource: formatIdsources(workflows),
                    })
                )
            } else {
                unknownWorkflows.push(...workflows)
            }
        }

        if (unknownWorkflows.length > 0) {
            messages.push(
                t("data_retrieval_in_progress", {
                    idsource: formatIdsources(unknownWorkflows),
                })
            )
        }

        if (delayedPdls.value.length > 0) {
            const idsources = delayedPdls.value
                .map((s) =>
                    formatIdSource(s.idsource as string, SOURCES_TYPES.PDL)
                )
                .join(", ")
            messages.push(
                t(
                    "cdc_activating_delayed",
                    { idsource: idsources },
                    delayedPdls.value.length
                )
            )
        }

        return messages
    })

    return {
        fetch,
        isFetching,
        hasError,
        getWorkflowEnedisMessageList,
    }
}
